import type { MTKeyFilterViewData } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTKeyFilterPanel } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { ROUTES } from '@/routes/routeConfig'
import { Box } from '@mui/material'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router'

/**
 * MTDefine - Layout container for the Define step (multi-territory).
 * Renders the MTKeyFilterPanel (Entity + TAN/UIN dropdowns + View button)
 * and an Outlet for sub-module content.
 *
 * Sub-module tabs (Section Service Keywords, Rule setting, etc.) are rendered by the
 * parent MTReturns component using MTKeyFilter - NOT duplicated here.
 *
 * Auto-redirects to the first sub-module when Define tab is clicked.
 */
const MTDefine = () => {
  const location = useLocation()
  // Get first sub-module from navigation API data for auto-redirect
  const { navModules } = useSelector((state: any) => state.navigation)

  const firstSubModuleCode = useMemo(() => {
    if (!navModules) return 'section_service_master'
    const returnsModule = navModules.find((mod: any) => mod.code === 'RETURNS')
    if (!returnsModule?.subModules) return 'section_service_master'
    const defineModule = returnsModule.subModules.find(
      (sub: any) => sub.code === 'define'
    )
    if (!defineModule?.subModules || defineModule.subModules.length === 0)
      return 'section_service_master'
    const sorted = [...defineModule.subModules].sort(
      (a: any, b: any) => a.order - b.order
    )
    return sorted[0].code || 'section_service_master'
  }, [navModules])

  const handleViewClick = (_filterData: MTKeyFilterViewData) => {
    // Filter data (including tanMasterUuid / entityMasterUuid) is available
    // to sub-pages via Redux state (viewTrigger + selectedEntityUuid/selectedTanUuid)
  }

  // Auto-redirect to the first sub-module if at exact /mt/returns/define
  const defineRoute = ROUTES.MT_RETURNS_DEFINE
  const isExactDefine =
    location.pathname === defineRoute || location.pathname === `${defineRoute}/`

  if (isExactDefine) {
    return <Navigate to={`${defineRoute}/${firstSubModuleCode}`} replace />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Multi-territory filter bar (Entity + TAN/UIN + View) */}
      <MTKeyFilterPanel onViewClick={handleViewClick} />

      {/* Sub-module content rendered via nested routes */}
      <Box sx={{ px: 0, py: 0 }}>
        <Outlet />
      </Box>
    </Box>
  )
}

export default MTDefine
